import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

interface TokenInsight {
  symbol: string;
  name: string;
  price: string;
  change24h: string;
  volume: string;
  liquidity: string;
  insight: string;
  chart: number[];
  isPositive: boolean;
  logo?: string;
}

interface AIInsightsProps {
  onTokenSelect?: (symbol: string) => void;
}

const AIInsights: React.FC<AIInsightsProps> = ({ onTokenSelect }) => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState<TokenInsight[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading AI insights
    setTimeout(() => {
      setInsights(generateMockInsights());
      setLoading(false);
    }, 1500);
  }, []);

  const generateMockInsights = (): TokenInsight[] => {
    const tokens = [
      {
        symbol: 'DINO',
        name: 'DinoToken',
        price: '$0.0234',
        change24h: '+11.31%',
        volume: '4h volume growth',
        liquidity: '$7.6M',
        insight: 'DINO surges with a 24h gain of +11.31% and impressive 4h volume growth, backed by robust liquidity of $7.6M.',
        isPositive: true,
        logo: '🦕'
      },
      {
        symbol: 'GOONER',
        name: 'GoonerCoin',
        price: '$0.1567',
        change24h: '+8.42%',
        volume: 'Strong momentum',
        liquidity: '$12.3M',
        insight: 'GOONER shows strong bullish momentum with significant whale accumulation and growing social sentiment.',
        isPositive: true,
        logo: '⚽'
      },
      {
        symbol: 'AVNT',
        name: 'Aventus',
        price: '$2.45',
        change24h: '-3.21%',
        volume: 'Consolidating',
        liquidity: '$45.2M',
        insight: 'AVNT in healthy consolidation phase after recent gains. Technical indicators suggest potential breakout.',
        isPositive: false,
        logo: '🚀'
      },
      {
        symbol: 'PEPE',
        name: 'Pepe',
        price: '$0.00001234',
        change24h: '+15.67%',
        volume: 'Viral trend',
        liquidity: '$89.1M',
        insight: 'PEPE experiencing viral social media momentum with massive volume spike and meme coin rotation.',
        isPositive: true,
        logo: '🐸'
      }
    ];

    return tokens.map(token => ({
      ...token,
      chart: generateMockChart(token.isPositive)
    }));
  };

  const generateMockChart = (isPositive: boolean): number[] => {
    const points = 20;
    const chart = [];
    let value = 50;
    
    for (let i = 0; i < points; i++) {
      const change = (Math.random() - 0.5) * 10;
      const trend = isPositive ? 0.5 : -0.2;
      value += change + trend;
      value = Math.max(10, Math.min(90, value));
      chart.push(value);
    }
    
    return chart;
  };

  const renderMiniChart = (data: number[], isPositive: boolean) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;
    
    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * 100;
      const y = 100 - ((value - min) / range) * 100;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg className="w-full h-16" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`gradient-${isPositive ? 'green' : 'red'}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={isPositive ? '#10b981' : '#ef4444'} stopOpacity="0.3" />
            <stop offset="100%" stopColor={isPositive ? '#10b981' : '#ef4444'} stopOpacity="0.1" />
          </linearGradient>
        </defs>
        <polyline
          fill={`url(#gradient-${isPositive ? 'green' : 'red'})`}
          stroke={isPositive ? '#10b981' : '#ef4444'}
          strokeWidth="1"
          points={`0,100 ${points} 100,100`}
        />
        <polyline
          fill="none"
          stroke={isPositive ? '#22c55e' : '#f87171'}
          strokeWidth="1.5"
          points={points}
        />
      </svg>
    );
  };

  const handleExpand = (symbol: string) => {
    navigate(`/orders/trading?token=${symbol}`);
  };

  const handleTokenClick = (symbol: string) => {
    if (onTokenSelect) {
      onTokenSelect(symbol);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-xl font-bold text-green-400">🧠 AI Insights</h2>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-20 bg-green-500/10 rounded-lg"></div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
            <span className="text-green-400">🧠</span>
          </div>
          <h2 className="text-xl font-bold text-green-400">AI Insights</h2>
        </div>
        <div className="text-xs text-green-400/60">
          Powered by GPT-4 & Live Data
        </div>
      </div>

      <div className="space-y-4">
        {insights.map((token, index) => (
          <Card 
            key={token.symbol}
            className={`p-4 transition-all duration-300 hover:scale-[1.02] cursor-pointer border ${
              token.isPositive 
                ? 'bg-gradient-to-r from-green-500/10 to-[#0a0a1a] border-green-500/30 hover:border-green-400/50' 
                : 'bg-gradient-to-r from-red-500/10 to-[#0a0a1a] border-red-500/30 hover:border-red-400/50'
            }`}
            onClick={() => handleTokenClick(token.symbol)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500/20 to-blue-500/20 flex items-center justify-center text-lg">
                  {token.logo}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-green-300 font-bold text-lg">{token.symbol}</h3>
                    <span className="text-green-400/70 text-sm">${token.symbol}</span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-green-400 font-medium">{token.price}</span>
                    <span className={`font-bold ${
                      token.isPositive ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {token.change24h}
                    </span>
                    <span className="text-green-400/60">{token.volume}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExpand(token.symbol);
                  }}
                  className="bg-green-600/30 hover:bg-green-500/40 text-green-300 border border-green-500/40 px-3 py-1 text-xs"
                >
                  <span className="mr-1">📈</span>
                  Trade
                </Button>
                <div className="flex space-x-1">
                  <Button
                    onClick={(e) => e.stopPropagation()}
                    className="bg-transparent hover:bg-green-500/10 text-green-400/70 p-1 w-8 h-8"
                  >
                    🔄
                  </Button>
                  <Button
                    onClick={(e) => e.stopPropagation()}
                    className="bg-transparent hover:bg-green-500/10 text-green-400/70 p-1 w-8 h-8"
                  >
                    ✨
                  </Button>
                </div>
              </div>
            </div>

            <p className="text-green-400/80 text-sm mb-3 leading-relaxed">
              {token.insight}
            </p>

            <div className="flex items-end justify-between">
              <div className="flex-1 mr-4">
                {renderMiniChart(token.chart, token.isPositive)}
              </div>
              <div className="text-right">
                <div className="text-green-400/70 text-xs">Liquidity</div>
                <div className="text-green-400 font-medium text-sm">{token.liquidity}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6 text-center">
        <Button 
          className="bg-green-600/30 hover:bg-green-500/40 text-green-300 border border-green-500/40"
          onClick={() => setInsights(generateMockInsights())}
        >
          🔄 Refresh Insights
        </Button>
      </div>
    </Card>
  );
};

export default AIInsights;
